import * as React from "react";
import CircleIcon from "@mui/icons-material/Circle";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { Button, Divider, Typography } from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator
} from "@mui/lab";
import billStyle from "./billing-styles";
import styles from "./billing.module.scss";
import useClientBills from "../../../common/hooks/useClientBills";
import { BillType } from "../../../../interfaces/BillInterfaces";
import Spinner from "../../../common/components/spinner";
import BillCollapse from "./bill-collapse";
import BillDetails from "./bill-details";
import BillShow from "../../../../containers/Billing/BillShow/BillShow";

const Summary = ({ row }: { row: BillType }) => {
  const { billNumber, client, total } = row;

  return (
    <div className={styles.billRow}>
      <Typography>{billNumber}</Typography>
      <Divider className={styles.divider} orientation="vertical" variant="middle" flexItem />
      <Typography>
        {client?.firstName} {client?.lastName}
      </Typography>
      <Divider className={styles.divider} orientation="vertical" variant="middle" flexItem />
      <Typography>{total}</Typography>
    </div>
  );
};

const BillRow = React.memo(({ row }: { row: BillType }): React.JSX.Element => {
  const [showBillDetails, setShowBillDetails] = React.useState(false);

  return (
    <div className={styles.billRow}>
      <BillCollapse summary={<Summary row={row} />}>
        <BillDetails row={row} />
        <Button
          onClick={() => setShowBillDetails((prev) => !prev)}
          className={styles.showMoreButton}
          variant="contained"
        >
          Show Details
        </Button>

        {showBillDetails && (
          <BillShow
            setShowMessagePanel={() => ({})}
            billId={row.id}
            handleViewClose={() => setShowBillDetails(false)}
            isForKitchenPharmacy={false}
            hasCreditNote={false}
            hideActions
            hideBillingActions
            billData={row}
          />
        )}
      </BillCollapse>
    </div>
  );
});

const Billing = (): React.JSX.Element => {
  const { bills, isLoading } = useClientBills();

  return (
    <div className={styles.billingContainer}>
      {isLoading && <Spinner />}
      {Object.keys(bills).map((key) => {
        const mapKey = key;
        return (
          <Timeline sx={billStyle.timeLine} key={mapKey}>
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot variant="filled" className={styles.timelineDot}>
                  <CircleIcon className={styles.circleIcon} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className={styles.headerContent}>{key}</TimelineContent>
            </TimelineItem>
            {bills[key].map((bill) => {
              const billId = bill.id;
              return (
                <TimelineItem key={billId} className={styles.bill}>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" className={styles.dotBorder}>
                      <LibraryBooksIcon />
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent className={styles.content}>
                    <BillRow row={bill} />
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        );
      })}
    </div>
  );
};

export default Billing;
